import {useEffect, useState} from "react";
import {useParams} from "react-router-dom";
import AssignUserModal from "../components/AssignUserModal";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";

import {
    Avatar,
    Box,
    Breadcrumbs,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Pagination,
    Paper,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";

import {useAlert} from "../context/AlertContext";
import AppTableHeaderCell from "../components/AppTableHeaderCell";
import {getCourseAccessUsers, getCourseById, unassignUserFromCourse} from "../api/api";
import {Course, User} from "../types";
import BreadcrumbLink from "../components/BreadcrumbLink";

const getInitials = (fullName?: string) => {
    if (!fullName || !fullName.trim()) return "?";

    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 1) {
        return parts[0].charAt(0).toUpperCase();
    }

    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
};

const CourseAccessDetailsPage = () => {
    const {courseId} = useParams<{ courseId?: string }>();
    const safeCourseId = Number(courseId || 0);

    const {showAlert} = useAlert();

    const [course, setCourse] = useState<Course | null>(null);
    const [courseLoading, setCourseLoading] = useState(false);

    const [users, setUsers] = useState<User[]>([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const [searchEmail, setSearchEmail] = useState("");
    const [searchInput, setSearchInput] = useState("");

    const [loading, setLoading] = useState(false);
    const [assignOpen, setAssignOpen] = useState(false);

    const [unassignOpen, setUnassignOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [unassignLoading, setUnassignLoading] = useState(false);

    const pageSize = 10;

    const fetchCourse = async () => {
        if (!safeCourseId) return;

        try {
            setCourseLoading(true);
            const c = await getCourseById(safeCourseId);
            setCourse(c);
        } catch (e: any) {
            showAlert(e?.message ?? "Failed to load course", "error");
            setCourse(null);
        } finally {
            setCourseLoading(false);
        }
    };

    const fetchUsers = async (uiPage: number, email: string) => {
        if (!safeCourseId) return;

        try {
            setLoading(true);
            const {users, totalPages} = await getCourseAccessUsers(
                safeCourseId,
                uiPage - 1,
                pageSize,
                email
            );

            setUsers(users);
            setTotalPages(totalPages);
            setPage(uiPage);
        } catch (e: any) {
            showAlert(e?.message ?? "Failed to load assigned users", "error");
            setUsers([]);
            setTotalPages(1);
            setPage(1);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!safeCourseId) return;
        fetchCourse();
        fetchUsers(1, "");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [safeCourseId]);

    useEffect(() => {
        setPage(1);
    }, [searchEmail]);

    const onSearch = () => {
        const email = searchInput.trim();
        setSearchEmail(email);
        fetchUsers(1, email);
    };

    const openUnassignConfirm = (user: User) => {
        setSelectedUser(user);
        setUnassignOpen(true);
    };

    const closeUnassignConfirm = () => {
        if (unassignLoading) return;
        setUnassignOpen(false);
        setSelectedUser(null);
    };

    const confirmUnassign = async () => {
        if (!selectedUser) return;

        try {
            setUnassignLoading(true);
            await unassignUserFromCourse(safeCourseId, selectedUser.id);

            showAlert("User unassigned successfully", "success");
            closeUnassignConfirm();
            await fetchUsers(page, searchEmail);
        } catch (e: any) {
            showAlert(e?.message ?? "Failed to unassign user", "error");
        } finally {
            setUnassignLoading(false);
        }
    };

    const refreshAssigned = () => fetchUsers(1, searchEmail);

    const courseTitle = course?.title ?? (courseLoading ? "Loading..." : "Course");

    return (
        <Box sx={{px: {xs: 1.5, md: 3}}}>
            <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small"/>}
                sx={{mb: 2}}
            >
                <BreadcrumbLink to="/">Courses</BreadcrumbLink>

                <Typography color="text.primary" variant="body2" sx={{fontWeight: 600}}>
                    Assigned Users
                </Typography>
            </Breadcrumbs>

            {(courseLoading || (loading && !users.length)) ? (
                <Box sx={{display: "flex", justifyContent: "center", py: 8}}>
                    <CircularProgress/>
                </Box>
            ) : (
                <>
                    <Paper
                        elevation={0}
                        sx={(theme) => ({
                            borderRadius: 3,
                            overflow: "hidden",
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                        })}
                    >
                        <Box
                            sx={(theme) => ({
                                display: "flex",
                                alignItems: {xs: "stretch", sm: "center"},
                                gap: 1.5,
                                px: {xs: 1.5, md: 3},
                                py: 2,
                                flexDirection: {xs: "column", sm: "row"},
                                backgroundColor: theme.palette.background.paper,
                                borderBottom: `1px solid ${theme.palette.action.hover}`,
                            })}
                        >
                            <Box sx={{display: "flex", flexDirection: "column", minWidth: 0}}>
                                <Typography sx={{fontWeight: 900}} noWrap>
                                    {courseTitle}
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{display: {xs: "none", md: "block"}}}
                                    noWrap
                                >
                                    {course?.description ? course.description : "No description"}
                                </Typography>

                                <Box
                                    sx={{
                                        display: {xs: "flex", md: "none"},
                                        flexWrap: "wrap",
                                        gap: 0.75,
                                        mt: 0.75,
                                    }}
                                >
                                    <Chip
                                        size="small"
                                        icon={<SchoolOutlinedIcon sx={{fontSize: 15}}/>}
                                        label={`Course ID: ${safeCourseId}`}
                                        sx={(theme) => ({
                                            height: 22,
                                            fontSize: 11,
                                            fontWeight: 700,
                                            backgroundColor: theme.palette.action.hover,
                                        })}
                                    />

                                    <Chip
                                        size="small"
                                        label={`${users.length} shown`}
                                        sx={(theme) => ({
                                            height: 22,
                                            fontSize: 11,
                                            fontWeight: 700,
                                            backgroundColor: theme.palette.action.hover,
                                        })}
                                    />
                                </Box>
                            </Box>

                            <Box sx={{flexGrow: 1, display: {xs: "none", sm: "block"}}}/>

                            <Box
                                sx={(theme) => ({
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: {xs: "space-between", sm: "flex-end"},
                                    gap: 1,
                                    px: {xs: 1.25, sm: 0},
                                    py: {xs: 1, sm: 0},
                                    borderRadius: {xs: 2.5, sm: 0},
                                    backgroundColor: {
                                        xs: theme.palette.action.hover,
                                        sm: "transparent",
                                    },
                                })}
                            >
                                <Typography variant="body2" color="text.secondary" sx={{fontWeight: 700}}>
                                    Page {page} of {totalPages}
                                </Typography>

                                <Typography variant="body2" color="text.secondary" sx={{fontWeight: 700}}>
                                    {users.length} shown
                                </Typography>
                            </Box>

                            <IconButton
                                onClick={() => setAssignOpen(true)}
                                sx={(theme) => ({
                                    width: 42,
                                    height: 42,
                                    borderRadius: "50%",
                                    backgroundColor: theme.palette.action.hover,
                                    color: theme.palette.text.primary,
                                    display: {xs: "none", sm: "inline-flex"},
                                    "&:hover": {
                                        backgroundColor: theme.palette.action.selected,
                                    },
                                })}
                            >
                                <AddIcon fontSize="small"/>
                            </IconButton>

                            <Button
                                onClick={() => setAssignOpen(true)}
                                variant="contained"
                                startIcon={<PersonAddAlt1Icon fontSize="small"/>}
                                sx={{
                                    display: {xs: "inline-flex", sm: "none"},
                                    borderRadius: 2,
                                    textTransform: "none",
                                    fontWeight: 800,
                                    py: 0.95,
                                }}
                            >
                                Assign user
                            </Button>
                        </Box>

                        <Box
                            sx={(theme) => ({
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                px: {xs: 1.5, md: 3},
                                py: 2,
                                flexWrap: "wrap",
                                backgroundColor: theme.palette.background.paper,
                                borderBottom: `1px solid ${theme.palette.action.hover}`,
                            })}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    width: {xs: "100%", sm: "auto"},
                                }}
                            >
                                <TextField
                                    type="search"
                                    size="small"
                                    placeholder="Search by email..."
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            onSearch();
                                        }
                                    }}
                                    sx={{
                                        width: {xs: "100%", sm: 320},
                                        flexGrow: {xs: 1, sm: 0},
                                    }}
                                />

                                <IconButton
                                    onClick={onSearch}
                                    disabled={loading}
                                    sx={(theme) => ({
                                        width: 40,
                                        height: 40,
                                        borderRadius: "50%",
                                        backgroundColor: theme.palette.action.hover,
                                        color: theme.palette.text.primary,
                                        flexShrink: 0,
                                        "&:hover": {
                                            backgroundColor: theme.palette.action.selected,
                                        },
                                    })}
                                >
                                    <SearchIcon fontSize="small"/>
                                </IconButton>
                            </Box>

                            <Box sx={{flexGrow: 1}}/>
                        </Box>

                        {users.length === 0 ? (
                            <Box sx={{py: 10, px: 3, textAlign: "center"}}>
                                <Typography variant="h6" sx={{mb: 1}}>
                                    No users found
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Try another email or assign a new user.
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                {/* Desktop table */}
                                <Box sx={{display: {xs: "none", md: "block"}, width: "100%", overflowX: "auto"}}>
                                    <table style={{width: "100%", borderCollapse: "separate", borderSpacing: 0}}>
                                        <thead>
                                        <tr style={{background: "#fafafa"}}>
                                            <AppTableHeaderCell>User</AppTableHeaderCell>
                                            <AppTableHeaderCell>Email</AppTableHeaderCell>
                                            <AppTableHeaderCell align="right">Actions</AppTableHeaderCell>
                                        </tr>
                                        </thead>

                                        <tbody>
                                        {users.map((u) => (
                                            <tr
                                                key={u.id}
                                                style={{
                                                    transition: "background-color 0.2s ease",
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.backgroundColor = "#fafafa";
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = "transparent";
                                                }}
                                            >
                                                <td style={{padding: "18px 16px"}}>
                                                    <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                                                        <Avatar
                                                            sx={{
                                                                width: 40,
                                                                height: 40,
                                                                fontSize: 13,
                                                                fontWeight: 700,
                                                                bgcolor: "grey.300",
                                                                color: "text.primary",
                                                            }}
                                                        >
                                                            {getInitials(u.fullName)}
                                                        </Avatar>

                                                        <Typography variant="body2" sx={{fontWeight: 600}}>
                                                            {u.fullName || "—"}
                                                        </Typography>
                                                    </Box>
                                                </td>

                                                <td style={{padding: "18px 16px"}}>
                                                    <Typography variant="body2">
                                                        {u.email}
                                                    </Typography>
                                                </td>

                                                <td style={{padding: "18px 16px", textAlign: "right"}}>
                                                    <Box sx={{display: "inline-flex", gap: 1}}>
                                                        <Tooltip title="Unassign user">
                                                            <IconButton
                                                                onClick={() => openUnassignConfirm(u)}
                                                                size="small"
                                                                disabled={unassignLoading}
                                                                sx={(theme) => ({
                                                                    width: 36,
                                                                    height: 36,
                                                                    borderRadius: "50%",
                                                                    backgroundColor: theme.palette.action.hover,
                                                                    color: theme.palette.text.primary,
                                                                    "&:hover": {
                                                                        backgroundColor: theme.palette.action.selected,
                                                                    },
                                                                })}
                                                            >
                                                                <RemoveCircleIcon fontSize="small" color="error"/>
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </Box>

                                {/* Mobile cards */}
                                <Box
                                    sx={{
                                        display: {xs: "block", md: "none"},
                                        p: 1.5,
                                    }}
                                >
                                    <Box
                                        sx={(theme) => ({
                                            mb: 1.5,
                                            p: 1.5,
                                            borderRadius: 3,
                                            background:
                                                theme.palette.mode === "dark"
                                                    ? "linear-gradient(135deg, rgba(30,41,59,0.95), rgba(15,23,42,0.95))"
                                                    : "linear-gradient(135deg, rgba(248,250,252,1), rgba(241,245,249,1))",
                                            border: `1px solid ${theme.palette.divider}`,
                                        })}
                                    >
                                        <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1}}>
                                            <Box sx={{minWidth: 0}}>
                                                <Typography sx={{fontWeight: 900, lineHeight: 1.2}}>
                                                    Course access users
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Manage users who can access this course
                                                </Typography>
                                            </Box>

                                            <Chip
                                                size="small"
                                                label={`${users.length} shown`}
                                                sx={(theme) => ({
                                                    fontWeight: 800,
                                                    backgroundColor: theme.palette.action.hover,
                                                    flexShrink: 0,
                                                })}
                                            />
                                        </Box>
                                    </Box>

                                    <Box sx={{display: "flex", flexDirection: "column", gap: 1.25}}>
                                        {users.map((u) => (
                                            <Box
                                                key={u.id}
                                                sx={(theme) => ({
                                                    p: 1.5,
                                                    borderRadius: 3,
                                                    border: `1px solid ${theme.palette.divider}`,
                                                    backgroundColor: theme.palette.background.paper,
                                                    boxShadow:
                                                        theme.palette.mode === "dark"
                                                            ? "0 14px 32px rgba(0,0,0,0.32)"
                                                            : "0 14px 32px rgba(15,23,42,0.07)",
                                                    overflow: "hidden",
                                                    position: "relative",
                                                    "&:before": {
                                                        content: '""',
                                                        position: "absolute",
                                                        top: 0,
                                                        bottom: 0,
                                                        left: 0,
                                                        width: 4,
                                                        backgroundColor: theme.palette.primary.main,
                                                    },
                                                })}
                                            >
                                                <Box sx={{display: "flex", alignItems: "flex-start", gap: 1.25}}>
                                                    <Avatar
                                                        sx={{
                                                            width: 46,
                                                            height: 46,
                                                            fontSize: 14,
                                                            fontWeight: 800,
                                                            bgcolor: "grey.300",
                                                            color: "text.primary",
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {getInitials(u.fullName)}
                                                    </Avatar>

                                                    <Box sx={{minWidth: 0, flexGrow: 1}}>
                                                        <Typography
                                                            variant="subtitle2"
                                                            sx={{
                                                                fontWeight: 900,
                                                                lineHeight: 1.35,
                                                                wordBreak: "break-word",
                                                            }}
                                                        >
                                                            {u.fullName || "—"}
                                                        </Typography>

                                                        <Box
                                                            sx={{
                                                                mt: 0.75,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 0.75,
                                                                minWidth: 0,
                                                            }}
                                                        >
                                                            <EmailOutlinedIcon
                                                                sx={{
                                                                    fontSize: 17,
                                                                    color: "text.secondary",
                                                                    flexShrink: 0,
                                                                }}
                                                            />

                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                                sx={{
                                                                    wordBreak: "break-word",
                                                                    minWidth: 0,
                                                                }}
                                                            >
                                                                {u.email || "—"}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Box>

                                                <Button
                                                    fullWidth
                                                    size="small"
                                                    variant="outlined"
                                                    color="error"
                                                    onClick={() => openUnassignConfirm(u)}
                                                    disabled={unassignLoading}
                                                    startIcon={<RemoveCircleIcon fontSize="small"/>}
                                                    sx={{
                                                        mt: 1.25,
                                                        borderRadius: 2,
                                                        textTransform: "none",
                                                        fontWeight: 800,
                                                        py: 0.85,
                                                    }}
                                                >
                                                    Unassign user
                                                </Button>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </>
                        )}
                    </Paper>

                    <Box sx={{display: "flex", justifyContent: "center", mt: 3}}>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(_, value) => fetchUsers(value, searchEmail)}
                            color="primary"
                            disabled={loading}
                        />
                    </Box>
                </>
            )}

            <AssignUserModal
                open={assignOpen}
                onClose={() => setAssignOpen(false)}
                courseId={safeCourseId}
                onAssigned={refreshAssigned}
            />

            <DeleteConfirmationModal
                open={unassignOpen}
                onClose={closeUnassignConfirm}
                onConfirm={confirmUnassign}
            />
        </Box>
    );
};

export default CourseAccessDetailsPage;